import { TSchedule } from '@/domain/Availability.domain';
import { cn } from '@/service/utils/cn';
import { getFormattedPrice } from '@/service/utils/getFormattedPrice';
import { AvailabilityInfo } from './AvailabilityInfo';
import { ArrowRightIcon } from '@/ui/common/Icons/ArrowRightIcon';
import { getCommonPhoneFormatURI } from '@/service/utils/getCommonPhoneFormat';

type TSearchSchedulesProps = {
    selectSchedule: (id: number) => void;
    showMore: boolean;
    partnerPhone: string;
    list?: TSchedule[];
    isLoading?: boolean;
};

const Skeleton = () => {
    return (
        <div>
            <div className="my-2 grid w-full animate-pulse grid-cols-2 gap-1.5 lg:grid-cols-3">
                <div className="h-[50px] w-full rounded-2xl bg-gray-4 dark:bg-blue-main"></div>
                <div className="h-[50px] w-full rounded-2xl bg-gray-4 dark:bg-blue-main"></div>
                <div className="h-[50px] w-full rounded-2xl bg-gray-4 dark:bg-blue-main"></div>
                <div className="h-[50px] w-full rounded-2xl bg-gray-4 dark:bg-blue-main"></div>
                <div className="hidden h-[50px] w-full rounded-2xl bg-gray-4 dark:bg-blue-main lg:block"></div>
                <div className="hidden h-[50px] w-full rounded-2xl bg-gray-4 dark:bg-blue-main lg:block"></div>
            </div>
        </div>
    );
};

export const SearchSchedules = ({
    selectSchedule,
    isLoading,
    partnerPhone,
    list,
    showMore,
}: TSearchSchedulesProps) => {
    if (isLoading) {
        return <Skeleton />;
    }

    if (!list || !list.length) {
        return (
            <div className="tx-large-semibold min-h-[106px] pt-8 text-gray-2 dark:text-gray-3">
                Online Booking Unavailable
                <br />
                Please select another date
            </div>
        );
    }

    const firstId = list?.[0]?.schedule_id;

    return (
        <div>
            <div
                className={cn(
                    'min-h-auto my-2 grid w-full grid-cols-2 gap-1.5 lg:grid-cols-3',
                    !showMore && 'max-h-[106px] overflow-hidden'
                )}
                id={'schedules-list' + firstId}
            >
                {list.map(({ schedule_id, time, price, tickets, call_to_booking }) => (
                    <div
                        className="flex h-[50px] w-full cursor-pointer items-center rounded-2xl border border-brand px-2 py-1"
                        key={schedule_id}
                        onClick={() =>
                            call_to_booking
                                ? (document.location.href = getCommonPhoneFormatURI(partnerPhone))
                                : selectSchedule(schedule_id)
                        }
                    >
                        <span
                            data-test-id="schedule-time"
                            className="tx-normal-medium h-10 w-10 flex-shrink-0 rounded-2xl bg-ash py-2 text-center leading-3 text-gray-2 dark:bg-blue-main dark:text-gray-3"
                        >
                            {time ? time.slice(0, time.length - 2) : 'All'}
                            <br />
                            {time ? time.slice(-2) : 'Day'}
                        </span>
                        <div className="mx-2 w-full">
                            {!!price && (
                                <span
                                    data-test-id="schedule-price"
                                    className="tx-normal-medium block w-full max-w-[100px] rounded-2xl bg-brand-light text-center text-gray-2 dark:bg-brand-dark dark:text-gray-3"
                                >
                                    {getFormattedPrice(price)}
                                </span>
                            )}
                            {!call_to_booking &&
                                (tickets.avail ? (
                                    <AvailabilityInfo
                                        className="w-full max-w-[100px] justify-center p-0 [&_span]:tx-normal-medium dark:bg-brand-dark [&_svg]:h-3.5  [&_svg]:w-3.5"
                                        ticketsAvail={tickets.avail ?? 0}
                                    />
                                ) : (
                                    <div className="h-[18px]"></div>
                                ))}
                            {call_to_booking && (
                                <div className="tx-normal-medium mt-0.5 flex h-[18px] w-full max-w-[100px] items-center whitespace-nowrap rounded-2xl border border-brand bg-ash pr-4 text-gray-2 dark:bg-blue-main dark:text-gray-3">
                                    <div className="h-[16px] w-[16px] min-w-[18px] rounded-2xl bg-brand">
                                        <img
                                            src="/icon/CallWhite.svg"
                                            alt="Call"
                                            height="15px"
                                            width="15px"
                                        />
                                    </div>
                                    <span className="ml-1.5">Call</span>
                                </div>
                            )}
                        </div>
                        <ArrowRightIcon className="h-4 w-4 [&_path]:fill-brand" />
                    </div>
                ))}
            </div>
        </div>
    );
};
